/**
 * Universal Navigation System - Techgacity Platform Meta-Routing
 *
 * Replaces manual app routing/navigation throughout the entire platform.
 * Vite generates direct routes for ALL components (unless excluded).
 * Database manifest (directory-navigation-manifest.json) drives all navigation.
 *
 * TWO NAVIGATION PATTERNS (determined by database):
 * 1. Button → Component: Direct route to component (Target_Dir_Sequence_Num = NULL)
 * 2. Button → Directory: Resolve directory → host component, pass dirSeq in state
 *
 * Database determines target type via Target_Dir_Sequence_Num:
 * - NULL = Component target
 * - NOT NULL = Directory target
 *
 * Created: 2026-06-06
 * Simplified: 2026-06-12 - Removed meta-routing complexity, renamed to handleManifestButtonClick
 */

import type { ManifestButton } from '../TGC/ComponentTemplates/DirectoryPatterns/manifest-types';
import directoryManifest from '../directory-navigation-manifest.json';
import generatedRoutes from '../generated-routes.json';

// Build sequence → route lookup map at module load (O(1) lookups)
// Use seq property directly from generated-routes.json (source of truth)
const routesBySeq = new Map<number, any>();
(generatedRoutes as any).routes.forEach((route: any) => {
    if (route.seq) {
        routesBySeq.set(route.seq, route);
    }
});

/**
 * Navigation target type discriminator
 * Use with navigateBySeq() to enforce explicit intent
 */
export type NavigationTargetType = 'directory' | 'component';

/**
 * Generic sequence-based navigation with explicit target type
 * Use this to enforce compile-time explicitness
 *
 * @param seq - Target sequence number
 * @param targetType - 'directory' or 'component' (forces explicit intent)
 * @param navigate - React Router navigate function
 *
 * @example
 * // ✅ Explicit - code reader knows intent
 * onClick={() => navigateBySeq(2, 'directory', navigate)}
 * onClick={() => navigateBySeq(10814, 'component', navigate)}
 *
 * // ❌ Forbidden - use specific function instead
 * onClick={() => navigate(999)}
 */
export function navigateBySeq(
    seq: number,
    targetType: NavigationTargetType,
    navigate: (path: string) => void,
): void {
    if (targetType === 'directory') {
        navigateToDirectory(seq, navigate);
    } else {
        navigateToComponent(seq, navigate);
    }
}

/**
 * UNIVERSAL NAVIGATION HANDLER - Techgacity Platform Meta-Routing
 *
 * Replaces manual app routing throughout entire platform.
 * Works with ANY button from directory-navigation-manifest.json.
 * Can be used in directory hosts, modules, tabs, or placed buttons.
 *
 * TWO NAVIGATION PATTERNS (determined by database field):
 *
 * 1. Button → Component (Target_Dir_Sequence_Num = NULL)
 *    - Route directly to component (mod_, scr_, mkt_, wdgt_, etc.)
 *    - Component mounts and renders itself
 *    - Example: Marketing site buttons, registry tabs, dashboard widgets
 *
 * 2. Button → Directory (Target_Dir_Sequence_Num != NULL)
 *    - Resolve directory → host component route
 *    - Pass dirSeq in state so host loads target directory's buttons/metadata
 *    - Host component mounts and calls useDirectoryManifest(dirSeq)
 *    - Example: Elevator buttons, navigation cards, directory traversal
 *
 * @param btn - ManifestButton from directory-navigation-manifest.json
 * @param navigate - React Router navigate function
 * @param currentDirSeq - Current directory sequence (for backTo context)
 * @param currentRoute - Current route path (for backTo navigation)
 */
export function handleManifestButtonClick(
    btn: ManifestButton,
    navigate: (path: string, options?: { state?: any }) => void,
    currentDirSeq?: number | null,
    currentRoute?: string,
): void {
    if (!btn.directRoute) {
        console.error('[handleManifestButtonClick] No directRoute in manifest for button:', btn);
        throw new Error(
            `Button "${btn.displayLabel}" has no directRoute in manifest. ` +
                `Run: npm run sync-manifests`,
        );
    }

    // Case 1: Button → Component (direct) - btn.directRoute is that component's
    // own static route, already registered by generateEncodedRoutes().
    if (btn.target_dir_sequence_num == null) {
        navigate(btn.directRoute);
        return;
    }

    // Case 2: Button → Directory. btn.directRoute here is the HOST component's
    // own static route (the same route for every directory sharing that host -
    // e.g. every card/elevator hosted by 10002 has this identical directRoute).
    // Navigate straight to it and pass dirSeq via state - no :dirSeq URL segment,
    // no re-derivation through directory-navigation-manifest.json at click-time.
    // useDirectoryHost.ts (LOCKED pattern) reads location.state.dirSeq on every
    // render, so the already-mounted host just re-renders with the new directory
    // - no remount, no Suspense re-trigger. See NAVIGATION_ARCHITECTURE_CLEANUP_JUNE_8_2026.md.
    navigate(btn.directRoute, {
        state: {
            dirSeq: btn.target_dir_sequence_num,
            backTo: currentRoute || window.location.pathname,
        },
    });
}

/**
 * Navigate to a DIRECTORY by sequence number
 * Use this in mod_/scr_ components when button targets a directory
 *
 * Directory will load its assigned host component (Pattern → Component mapping)
 * Resolves: Directory_Sequence_Num → Host_Component_Sequence_Num → /component/{hostSeq}
 *
 * @param directorySeq - Target directory sequence (0, 1, 2, 3...)
 * @param navigate - React Router navigate function
 * @param currentRoute - Optional current route for backTo context
 *
 * @example
 * // Button navigates to "TGC-02 Elevator" directory (resolves to host component 10002)
 * onClick={() => navigateToDirectory(2, navigate)}
 */
export function navigateToDirectory(
    directorySeq: number,
    navigate: (path: string, options?: { state?: any }) => void,
    currentRoute?: string,
): void {
    // Load directory manifest and find directory by sequence
    const manifest = directoryManifest as any;

    // Manifest structure: { directories: { "0": {...}, "1": {...} } }
    const directory = manifest.directories?.[directorySeq.toString()];

    if (!directory) {
        console.error(`❌ Directory sequence ${directorySeq} not found in manifest`);
        console.error('   Available directories:', Object.keys(manifest.directories || {}));
        return;
    }

    // Extract host component sequence (field: componentSequenceNum)
    const hostComponentSeq = directory.componentSequenceNum;

    if (!hostComponentSeq) {
        console.error(`❌ Directory ${directorySeq} has no host component assigned`);
        return;
    }

    // Look up direct route for host component
    const routeData = routesBySeq.get(hostComponentSeq);
    if (!routeData) {
        console.error(`❌ Host component ${hostComponentSeq} not found in route manifest`);
        return;
    }

    // Navigate straight to the host component's own static route (already
    // registered by generateEncodedRoutes()) and pass dirSeq via state.
    // useDirectoryHost.ts (LOCKED pattern) reads location.state.dirSeq on every
    // render - no :dirSeq URL segment needed, no remount required.
    navigate(routeData.directRoute, {
        state: {
            dirSeq: directorySeq,
            backTo: currentRoute || window.location.pathname,
        },
    });
}

/**
 * Navigate to the APP DEFAULT directory ("front door") - reads
 * directory-navigation-manifest.json's top-level `appDefault` field
 * (currently "0" = Techgacity Center Welcome) rather than hardcoding a
 * directory sequence at the call site. This is the same directory
 * App.tsx's "/" route + DefaultLandingLoader.tsx resolve to via the
 * database (Is_App_Default = 1), so a Home/logo button using this
 * function always lands wherever the platform's actual front door is
 * configured to be - even if that changes in the database later.
 *
 * @example
 * // Home icon in PlatformHeader
 * onClick={() => navigateToAppDefault(navigate)}
 */
export function navigateToAppDefault(
    navigate: (path: string, options?: { state?: any }) => void,
    currentRoute?: string,
): void {
    const manifest = directoryManifest as any;
    const appDefaultSeq = Number(manifest.appDefault);

    if (Number.isNaN(appDefaultSeq)) {
        console.error(
            `❌ appDefault "${manifest.appDefault}" in directory-navigation-manifest.json is not a valid directory sequence. Run: npm run sync-manifests`,
        );
        return;
    }

    navigateToDirectory(appDefaultSeq, navigate, currentRoute);
}

/**
 * Navigate to a COMPONENT by sequence number
 * Use this in mod_/scr_ components when button targets a functional component
 *
 * @param componentSeq - Target component sequence (10000, 10814, etc.)
 * @param navigate - React Router navigate function
 * @param queryParams - Optional query string params to carry along (e.g. meetingId,
 *   sessionId, brand) - hard-wired direct-route + context, no directory lookup involved.
 * @param state - Optional React Router location.state payload to carry along
 *   (e.g. { activeTab, solutionFilter } or { consultantSessionUuid }) for
 *   components that read context from location.state instead of query params.
 *
 * @example
 * // Button navigates directly to Organization Registry component
 * onClick={() => navigateToComponent(10814, navigate)}
 *
 * // Button navigates to a component carrying context (e.g. joining a round table)
 * onClick={() => navigateToComponent(11064, navigate, { meetingId: 'RT-001', sessionId, brand: orgUuid })}
 *
 * // Button navigates to a component carrying router state
 * onClick={() => navigateToComponent(10299, navigate, undefined, { activeTab: 10503 })}
 */
export function navigateToComponent(
    componentSeq: number,
    navigate: (path: string, options?: { state?: any }) => void,
    queryParams?: Record<string, string | number | undefined>,
    state?: any,
): void {
    // Verify component exists in route manifest
    const routeData = routesBySeq.get(componentSeq);
    if (!routeData) {
        console.error(`❌ Component ${componentSeq} not found in route manifest`);
        return;
    }
    let route = routeData.directRoute;

    if (queryParams) {
        const params = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.set(key, String(value));
            }
        });
        const query = params.toString();
        if (query) {
            route = `${route}?${query}`;
        }
    }

    navigate(route, state !== undefined ? { state } : undefined);
}

/**
 * PLACED BUTTON - ad-hoc button placement anywhere in the codebase
 *
 * Same click behavior as a manifest-rendered directory/elevator/card button
 * (handleManifestButtonClick), but for a button you're hand-placing in JSX
 * that ISN'T part of a directory's buttons[] array - e.g. a Card inside a
 * modal, a standalone dashboard shortcut, etc. Label/description are
 * auto-resolved from the existing manifests (directory-navigation-manifest.json
 * for directories, generated-routes.json for components) so callers don't
 * hand-type text that can drift from the source of truth.
 *
 * @example
 * // Directory target (resolves to host component + dirSeq state)
 * const btn = resolvePlacedButton({ directory: 2 }, navigate, location.pathname);
 * <Card onClick={btn.onClick}>{btn.label}</Card>
 *
 * @example
 * // Component/tsx target (direct route)
 * const btn = resolvePlacedButton({ tsx: 10023 }, navigate);
 * <Card onClick={btn.onClick}>{btn.label}</Card>
 *
 * @example
 * // Just need the click behavior, already have your own label/UI
 * <Card onClick={() => placedButtonOnClick({ tsx: 10023 }, navigate)}>New User? Sign Up</Card>
 */
export type PlacedButtonTarget = { directory: number } | { tsx: number };

export interface PlacedButtonInfo {
    label: string;
    description: string | null;
    directRoute: string;
    onClick: () => void;
}

/**
 * Derive a human-readable label from a componentPath when no manifest label
 * exists (component/tsx targets have no per-assignment displayLabel - that's
 * an attribute of a directory's button assignment, not of the component itself).
 * "TGC/RegisteredTabs/scr_tgc_10849_quick_help_atm" -> "Quick Help Atm"
 */
function deriveLabelFromComponentPath(componentPath: string): string {
    const fileName = componentPath.split('/').pop() || componentPath;
    const match = fileName.match(/^[a-z]+_[a-z]+_\d+_(.+)$/);
    const slug = match ? match[1] : fileName;
    return slug
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Resolve a placed button's label/description/route + ready-to-use onClick,
 * reading from the same manifests the directory host patterns use.
 */
export function resolvePlacedButton(
    target: PlacedButtonTarget,
    navigate: (path: string, options?: { state?: any }) => void,
    currentRoute?: string,
): PlacedButtonInfo {
    if ('directory' in target) {
        const manifest = directoryManifest as any;
        const directory = manifest.directories?.[target.directory.toString()];
        if (!directory) {
            throw new Error(
                `[resolvePlacedButton] Directory ${target.directory} not found in directory-navigation-manifest.json. Run: npm run sync-manifests`,
            );
        }

        const hostRoute = routesBySeq.get(directory.componentSequenceNum);
        if (!hostRoute) {
            throw new Error(
                `[resolvePlacedButton] Host component ${directory.componentSequenceNum} for directory ${target.directory} not found in generated-routes.json`,
            );
        }

        return {
            label:
                directory.directoryName || directory.headerText || `Directory ${target.directory}`,
            description: directory.directoryDescription || directory.headerSubtitle || null,
            directRoute: hostRoute.directRoute,
            onClick: () => navigateToDirectory(target.directory, navigate, currentRoute),
        };
    }

    const routeData = routesBySeq.get(target.tsx);
    if (!routeData) {
        throw new Error(
            `[resolvePlacedButton] Component ${target.tsx} not found in generated-routes.json. Run: npm run sync-manifests`,
        );
    }

    return {
        label: deriveLabelFromComponentPath(routeData.componentPath),
        description: null,
        directRoute: routeData.directRoute,
        onClick: () => navigateToComponent(target.tsx, navigate),
    };
}

/**
 * Convenience alias - just the click behavior (no label/description needed).
 * Use resolvePlacedButton() instead when the button also needs to display
 * manifest-derived label/description text.
 */
export function placedButtonOnClick(
    target: PlacedButtonTarget,
    navigate: (path: string, options?: { state?: any }) => void,
    currentRoute?: string,
): void {
    resolvePlacedButton(target, navigate, currentRoute).onClick();
}

/**
 * Navigate to a COMPONENT by filename (legacy/explicit)
 * Use when you know exact TSX filename
 *
 * @param componentFileName - Target component TSX filename
 * @param navigate - React Router navigate function
 *
 * @example
 * // Button navigates to specific component by filename
 * onClick={() => navigateToComponentByFilename('mod_tgc_10814_organization_registry.tsx', navigate)}
 */
export function navigateToComponentByFilename(
    componentFileName: string,
    navigate: (path: string) => void,
): void {
    const route = `/${componentFileName.replace('.tsx', '')}`;
    navigate(route);
}

/**
 * Debug helper - log navigation context
 */
export function debugButtonTarget(btn: ManifestButton): void {
    if (btn.target_dir_sequence_num != null) {
        console.log('🗂️ Directory Target:', {
            context: 'Directory → Directory',
            directorySeq: btn.target_dir_sequence_num,
            hostComponent: btn.tsx_file_name_target,
        });
    } else {
        console.log('📄 Component Target:', {
            context: 'Directory → Component',
            componentSeq: btn.child_sequence_num,
            targetComponent: btn.tsx_file_name_target,
        });
    }
}

/**
 * @deprecated Use handleManifestButtonClick instead
 * Backward-compatible alias for existing code during transition
 */
export const handleDirectoryButtonClick = handleManifestButtonClick;
